import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/site";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { spotlightTilt } from "@/components/site/fx";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const ServiceCard = ({ s, i }) => {
  const Icon = s.icon;
  const { lang } = useI18n();
  const title = lang === "en" ? s.title_en : s.title;
  const desc = lang === "en" ? s.desc_en : s.desc;
  return (
    <motion.div
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: EASE, delay: (i % 3) * 0.08 }}
      className={s.feature ? "sm:col-span-2 lg:col-span-2" : ""}
    >
      <article
        {...spotlightTilt(7)}
        style={{ willChange: "transform" }}
        className="spotlight card-edge group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl p-7"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-neon/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />

        <div className="relative flex items-start justify-between">
          <span className="grid h-14 w-14 place-items-center border border-white/10 bg-ink-900 text-white/70 transition-colors duration-300 group-hover:border-cyan-neon/40 group-hover:text-cyan-neon">
            <Icon size={26} strokeWidth={1.6} />
          </span>
          <span className="font-mono text-xs text-white/25 transition-colors duration-300 group-hover:text-cyan-neon">
            {s.id}
          </span>
        </div>

        <div className="relative mt-8">
          <h3 className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">
            {title}
          </h3>
          <p className="mt-3 font-body text-sm leading-relaxed text-white/55">
            {desc}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {s.tags.map((t) => (
              <span
                key={t}
                className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/45"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <ArrowUpRight
          size={18}
          className="absolute bottom-7 right-7 text-white/20 transition-all duration-300 group-hover:text-cyan-neon"
        />
      </article>
    </motion.div>
  );
};

export const Services = () => {
  const { t } = useI18n();
  return (
    <section
      id="servicii"
      data-testid="services-section"
      className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32"
    >
      <div className="max-w-3xl">
        <Reveal>
          <SectionLabel index="[ 01 ]">{t("srv.label")}</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("srv.h1")}{" "}
            <span className="text-stroke">{t("srv.hw1")}</span> {t("srv.h2")}{" "}
            <span className="text-aurora">{t("srv.hw2")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
            {t("srv.sub")}
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} s={s} i={i} />
        ))}
      </div>
    </section>
  );
};
